import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../hive/ui/Icon';
import { useFairTracker } from '../data/FairTrackerStore';
import { Empty, Field, Input, Modal, Pill, Select, useToast } from '../ui';
import { newFollowUp } from '../data/entities';
import { FU_STATUS, FU_TYPES } from '../data/constants';
import { dateLabel, expiryLabel, priceLine, quotesExpiring } from '../rules';
import { p } from '../routes';
import { tr } from '../i18n';

/**
 * Follow-ups (spec § 5.14) — every open thread across the whole fair.
 *
 * The "Quotes expiring" card sits on top because it is the only time-critical
 * thing here: a quote that lapses has to be re-asked for, and rule E1's
 * 14-day window is the warning.
 */
export default function FollowupsScreen() {
  const toast = useToast();
  const { fair, visibleSuppliers, saveSupplier } = useFairTracker();
  const [showAll, setShowAll] = useState(false);
  const [adding, setAdding] = useState(false);

  const expiring = quotesExpiring(visibleSuppliers);

  const rows = visibleSuppliers
    .flatMap((s) => (s.followUps || []).map((f) => ({ supplier: s, followUp: f })))
    .filter(({ followUp }) => showAll || followUp.status !== 'Done')
    .sort((a, b) =>
      `${a.followUp.date || '9999'}${a.followUp.time || ''}`.localeCompare(
        `${b.followUp.date || '9999'}${b.followUp.time || ''}`,
      ),
    );

  const setStatus = async (supplier, followUp, status) => {
    try {
      await saveSupplier({
        ...supplier,
        followUps: (supplier.followUps || []).map((f) => (f.id === followUp.id ? { ...f, status } : f)),
      });
    } catch (e) {
      toast('Could not save', 'error');
    }
  };

  const add = async ({ supplierId, followUp }) => {
    const supplier = visibleSuppliers.find((s) => s.id === supplierId);
    if (!supplier) return;
    try {
      await saveSupplier({ ...supplier, followUps: [...(supplier.followUps || []), followUp] });
      setAdding(false);
      toast('Follow-up added');
    } catch (e) {
      toast('Could not save', 'error');
    }
  };

  return (
    <div className="ft-page">
      <div className="ft-title">
        <h1 className="ft-h1">{tr("Follow-ups")}</h1>
        <p className="ft-sub" style={{ margin: 0 }}>
          {fair ? fair.name : ''} · {rows.length} {showAll ? tr('total') : tr('open')}
        </p>
      </div>

      {expiring.length > 0 && (
        <section className="ft-card">
          <h2 className="ft-h2">{tr("Quotes expiring")}</h2>
          <p className="ft-sub">{tr("Within 14 days. Ask for a fresh quotation before it lapses.")}</p>
          <div className="ft-list ft-list-rows">
            {expiring.map(({ supplier, product, days }) => (
              <Link
                className="ft-rowcard"
                key={`${supplier.id}-${product.id}`}
                to={`${p(`/s/${supplier.id}`)}?tab=pricing`}
              >
                <div className="ft-rowcard-main">
                  <b>{supplier.company}</b>
                  <div className="ft-faint">
                    {product.name} · {priceLine(product, fair) || tr('no price')} · {tr('valid until')}{' '}
                    {dateLabel(product.validUntil)}
                  </div>
                </div>
                <Pill tone={days < 0 ? 'no' : days <= 3 ? 'maybe' : 'info'}>{expiryLabel(days)}</Pill>
              </Link>
            ))}
          </div>
        </section>
      )}

      <div className="ft-row" style={{ marginTop: 12 }}>
        <button
          type="button"
          className={`ft-btn${!showAll ? ' ft-btn-primary' : ''}`}
          onClick={() => setShowAll(false)}
        >
          {tr("Pending")}
        </button>
        <button
          type="button"
          className={`ft-btn${showAll ? ' ft-btn-primary' : ''}`}
          onClick={() => setShowAll(true)}
        >
          {tr("All")}
        </button>
        <button type="button" className="ft-btn ft-btn-gold" onClick={() => setAdding(true)}>
          <Icon name="Plus" size={16} /> {tr("New follow-up")}
        </button>
      </div>

      {!rows.length && (
        <Empty title={showAll ? 'No follow-ups at all' : 'Nothing outstanding'}>
          {tr("A next step or an appointment set in Quick Entry creates one of these automatically.")}
        </Empty>
      )}

      <div className="ft-list ft-list-rows" style={{ marginTop: 12 }}>
        {rows.map(({ supplier, followUp }) => (
          <div className="ft-rowcard" key={followUp.id}>
            <div className="ft-rowcard-main">
              <Link to={`${p(`/s/${supplier.id}`)}?tab=follow`}>
                <b>{supplier.company}</b>
              </Link>
              <div className="ft-rowcard-pills">
                <Pill tone="info">{followUp.type}</Pill>
                {followUp.auto && <span className="ft-faint">{tr("auto")}</span>}
              </div>
              <div className="ft-faint">
                {[dateLabel(followUp.date), followUp.time, followUp.notes].filter(Boolean).join(' · ')}
              </div>
            </div>
            <Select
              value={followUp.status}
              options={FU_STATUS}
              onChange={(v) => setStatus(supplier, followUp, v)}
            />
          </div>
        ))}
      </div>

      {adding && (
        <NewFollowUp suppliers={visibleSuppliers} onSave={add} onClose={() => setAdding(false)} />
      )}
    </div>
  );
}

function NewFollowUp({ suppliers, onSave, onClose }) {
  const [supplierId, setSupplierId] = useState((suppliers[0] || {}).id || '');
  const [f, setF] = useState(() => newFollowUp());
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));

  return (
    <Modal
      title={tr("New follow-up")}
      onClose={onClose}
      actions={
        <>
          <button type="button" className="ft-btn ft-btn-cancel" onClick={onClose}>
            {tr("Cancel")}
          </button>
          <button
            type="button"
            className="ft-btn ft-btn-green"
            disabled={!supplierId}
            onClick={() => onSave({ supplierId, followUp: f })}
          >
            {tr("Add follow-up")}
          </button>
        </>
      }
    >
      <Field label={tr("Supplier")}>
        <Select
          value={supplierId}
          options={suppliers.map((s) => ({
            value: s.id,
            label: s.company,
          }))}
          onChange={setSupplierId}
        />
      </Field>
      <Field label={tr("Type")}>
        <Select value={f.type} options={FU_TYPES} onChange={set('type')} />
      </Field>
      <div className="ft-grid-2">
        <Field label={tr("Date")}>
          <Input type="date" value={f.date} onChange={set('date')} />
        </Field>
        <Field label={tr("Time")}>
          <Input type="time" value={f.time} onChange={set('time')} />
        </Field>
      </div>
      <Field label={tr("Status")}>
        <Select value={f.status} options={FU_STATUS} onChange={set('status')} />
      </Field>
      <Field label={tr("Notes")}>
        <Input value={f.notes} onChange={set('notes')} />
      </Field>
    </Modal>
  );
}
